import React from "react";
import { motion } from "framer-motion";
import brandItems from "../data/BrandItems";
import { useLanguage } from "../context/LanguageContext";

const PHONE = "584228180393";
const buildWhatsAppURL = (message) =>
  `https://wa.me/${PHONE}?text=${encodeURIComponent(message)}`;

const HeroSectionB2B = () => {
  const { t } = useLanguage();
  const whatsappAuditMsg = t("heroB2B.whatsappAuditMsg");

  return (
    <header className="relative w-full px-6 pt-6 pb-12 overflow-hidden">
      {/* GLOW DECORATIVO DE FONDO */}
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[720px] h-[340px] bg-gradient-to-tr from-cyan-500/20 via-blue-600/15 to-purple-600/10 blur-[130px] rounded-full" />

      <div className="max-w-7xl mx-auto space-y-10 relative z-10">
        {/* TARJETA MAESTRA DEL HERO */}
        <div
          className="
            relative
            rounded-[2.5rem]
            overflow-hidden
            border-2
            border-slate-200
            dark:border-white/10
            bg-white
            dark:bg-gradient-to-br
            dark:from-[#08131F]
            dark:via-[#0D1D2E]
            dark:to-[#091522]
            shadow-xl
            dark:shadow-2xl
            p-8
            md:p-14
            backdrop-blur-xl
            transition-colors
            duration-300
          "
        >
          {/* LUZ INTERNA DECORATIVA */}
          <div className="pointer-events-none absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 dark:bg-cyan-500/15 blur-3xl rounded-full" />
          <div className="pointer-events-none absolute bottom-0 left-0 w-80 h-80 bg-blue-600/10 dark:bg-blue-600/15 blur-3xl rounded-full" />

          <div className="relative z-10 grid lg:grid-cols-12 gap-12 items-center">
            {/* COLUMNA IZQUIERDA: COPY ESTRATÉGICO B2B */}
            <div className="lg:col-span-7 space-y-6">
              {/* BADGE DE CATEGORÍA */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-700 dark:text-cyan-300 font-extrabold text-xs uppercase tracking-wider shadow-sm">
                <span className="w-2 h-2 rounded-full bg-cyan-500 animate-ping" />
                <span>{t("heroB2B.badge")}</span>
              </div>

              {/* TÍTULO PRINCIPAL */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem] font-black tracking-tight text-slate-900 dark:text-white leading-[1.12]">
                {t("heroB2B.title")}
              </h1>

              {/* SUBTÍTULO */}
              <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl font-normal">
                {t("heroB2B.subtitle")}
              </p>

              {/* CTAS DE CONVERSIÓN */}
              <div className="flex flex-wrap items-center gap-4 pt-2">
                <a
                  href={buildWhatsAppURL(whatsappAuditMsg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="
                    px-8
                    py-4
                    rounded-2xl
                    bg-gradient-to-r
                    from-cyan-500
                    to-blue-600
                    hover:from-cyan-400
                    hover:to-blue-500
                    text-white
                    font-black
                    text-sm
                    shadow-[0_10px_30px_rgba(6,182,212,0.35)]
                    transition-all
                    duration-300
                    transform
                    hover:-translate-y-0.5
                    flex
                    items-center
                    gap-2.5
                  "
                >
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12c0 1.82.49 3.53 1.34 5L2 22l5.14-1.31C8.61 21.53 10.26 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm0 18c-1.57 0-3.04-.44-4.29-1.2l-.31-.18-3.04.77.81-2.96-.2-.33C4.38 14.88 4 13.48 4 12c0-4.41 3.59-8 8-8s8 3.59 8 8-3.59 8-8 8z" />
                  </svg>
                  <span>{t("heroB2B.ctaAudit")}</span>
                </a>

                <a
                  href="#servicios"
                  className="
                    px-7
                    py-4
                    rounded-2xl
                    border-2
                    border-slate-300
                    dark:border-white/15
                    bg-white/80
                    dark:bg-white/5
                    hover:bg-slate-100
                    dark:hover:bg-white/10
                    text-slate-800
                    dark:text-white
                    font-extrabold
                    text-sm
                    transition
                    duration-300
                    flex
                    items-center
                    gap-2
                  "
                >
                  <span>{t("heroB2B.ctaServices")}</span>
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
                  </svg>
                </a>
              </div>

              {/* TRUST SIGNALS RÁPIDOS */}
              <div className="pt-6 border-t border-slate-200 dark:border-white/10 grid grid-cols-3 gap-4 text-left">
                <div>
                  <div className="text-2xl sm:text-3xl font-black text-cyan-600 dark:text-cyan-400">
                    {t("heroB2B.statUsers")}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                    {t("heroB2B.statUsersLabel")}
                  </p>
                </div>

                <div>
                  <div className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400">
                    {t("heroB2B.statApproval")}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                    {t("heroB2B.statApprovalLabel")}
                  </p>
                </div>

                <div>
                  <div className="text-2xl sm:text-3xl font-black text-blue-600 dark:text-blue-400">
                    {t("heroB2B.statSla")}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                    {t("heroB2B.statSlaLabel")}
                  </p>
                </div>
              </div>
            </div>

            {/* COLUMNA DERECHA: PREVIEW DE CÓDIGO & ARQUITECTURA */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="w-full max-w-md rounded-3xl border-2 border-slate-300/80 dark:border-cyan-500/30 bg-[#0A131F] text-white p-6 shadow-2xl space-y-4">
                {/* WINDOW HEADER */}
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500/80 inline-block" />
                    <span className="w-3 h-3 rounded-full bg-yellow-500/80 inline-block" />
                    <span className="w-3 h-3 rounded-full bg-emerald-500/80 inline-block" />
                  </div>
                  <span className="text-[11px] font-mono text-cyan-400 font-semibold">
                    henki_core_audit.dart
                  </span>
                </div>

                {/* CODE SNIPPET VISUAL */}
                <div className="font-mono text-xs leading-relaxed space-y-2 text-slate-300">
                  <p className="text-slate-400">{t("heroB2B.codeComment")}</p>
                  <p>
                    <span className="text-purple-400">class</span>{" "}
                    <span className="text-cyan-300">AppRescueEngine</span> {"{"}
                  </p>
                  <div className="pl-4 space-y-1">
                    <p>
                      <span className="text-blue-400">final</span> status ={" "}
                      <span className="text-emerald-300">"Production_Ready"</span>;
                    </p>
                    <p>
                      <span className="text-blue-400">int</span> testersActive ={" "}
                      <span className="text-amber-300">12</span>;
                    </p>
                    <p>
                      <span className="text-blue-400">Duration</span> sla ={" "}
                      <span className="text-cyan-300">Duration</span>(hours:{" "}
                      <span className="text-amber-300">24</span>);
                    </p>
                    <p className="text-slate-400 pt-1">
                      <span className="text-purple-400">void</span>{" "}
                      <span className="text-yellow-300">diagnoseAndShield</span>() {"{"}
                    </p>
                    <p className="pl-4 text-emerald-400">
                      ✓ ZeroMemoryLeaks();
                    </p>
                    <p className="pl-4 text-emerald-400">
                      ✓ HardenedApiKeys();
                    </p>
                    <p className="pl-4 text-emerald-400">
                      ✓ GooglePlayCompliance();
                    </p>
                    <p className="text-slate-400">{"}"}</p>
                  </div>
                  <p>{"}"}</p>
                </div>

                {/* BOTTOM STATUS BAR */}
                <div className="pt-3 border-t border-white/10 flex items-center justify-between text-[11px] text-slate-400">
                  <span className="flex items-center gap-1.5 text-emerald-400 font-semibold">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block animate-pulse" />
                    {t("heroB2B.codeStatus")}
                  </span>
                  <span className="font-mono">v2.4.0</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BARRA DE MARCAS Y ALIADOS INTEGRADA (MARQUEE INFINITO A LA DERECHA) */}
        <div className="space-y-4 pt-2">
          <div className="flex items-center justify-center gap-3">
            <span className="h-[1px] w-12 bg-slate-300 dark:bg-white/10" />
            <p className="text-xs sm:text-sm font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 text-center">
              {t("heroB2B.trustedBy")}
            </p>
            <span className="h-[1px] w-12 bg-slate-300 dark:bg-white/10" />
          </div>

          {/* CONTENEDOR CON MÁSCARA DE DESVANECIMIENTO LATERAL */}
          <div className="relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)] py-2">
            <motion.div
              className="flex items-center gap-5 w-max"
              animate={{ x: ["-50%", "0%"] }}
              transition={{
                x: {
                  repeat: Infinity,
                  repeatType: "loop",
                  duration: 38,
                  ease: "linear",
                },
              }}
            >
              {[...brandItems, ...brandItems, ...brandItems, ...brandItems].map((brand, index) => {
                const cardContent = (
                  <div
                    className="
                      group
                      relative
                      min-w-[190px]
                      sm:min-w-[210px]
                      h-24
                      px-4
                      py-2.5
                      rounded-2xl
                      border-2
                      border-slate-200
                      dark:border-white/10
                      bg-white
                      dark:bg-[#0c1a27]
                      hover:border-cyan-500
                      dark:hover:border-cyan-400
                      transition-all
                      duration-300
                      flex
                      flex-col
                      items-center
                      justify-center
                      shadow-sm
                      hover:shadow-md
                      hover:-translate-y-1
                      shrink-0
                    "
                  >
                    {/* CONTENEDOR AMPLIO DE CONTRASTE DEL LOGO */}
                    <div className="h-11 w-full rounded-xl bg-slate-900 dark:bg-black/70 border border-slate-700/50 dark:border-white/15 flex items-center justify-center px-3 py-1.5 shadow-inner">
                      <img
                        src={brand.src}
                        alt={brand.alt}
                        className="max-h-8 sm:max-h-9 max-w-[90%] object-contain filter drop-shadow-sm opacity-95 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                    <span className="text-[11px] sm:text-xs font-extrabold text-slate-800 dark:text-slate-200 truncate max-w-full group-hover:text-cyan-600 dark:group-hover:text-cyan-400 mt-1.5">
                      {brand.alt}
                    </span>
                  </div>
                );

                return brand.url ? (
                  <a
                    key={`${brand.alt}-${index}`}
                    href={brand.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block outline-none"
                    title={brand.alt}
                  >
                    {cardContent}
                  </a>
                ) : (
                  <div key={`${brand.alt}-${index}`} title={brand.alt}>
                    {cardContent}
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default HeroSectionB2B;
